/**
 * API base URL adapter — single source of truth for external service endpoints.
 *
 * BROWSER: routes via nginx proxy (e.g. /api/openai/v1/chat/completions).
 *          Auth headers added by nginx server-side from runtime envs.
 *          Plus X-Workspace header from localStorage (Phase 3 C2, 2026-05-26)
 *          dla per-workspace key routing.
 * WORKER (Node): routes directly to upstream (e.g. https://api.openai.com).
 *                Auth headers added in code from process.env.
 *                Workspace dostaje przez job payload (Phase 3.5 backlog).
 *
 * Usage:
 *   const { url, headers } = getApiTarget("openai", "/v1/chat/completions");
 *   await fetch(url, { method: "POST", headers, body });
 */

import { isNodeRuntime } from "./env";
import { getActiveWorkspace } from "./workspace";

type ApiKey =
  | "openai"
  | "anthropic"
  | "fal"
  | "senuto"
  | "crawl4ai"
  | "dataforseo";

interface ApiTarget {
  url: string;
  headers: Record<string, string>;
}

const BROWSER_BASE: Record<ApiKey, string> = {
  openai: "/api/openai",
  anthropic: "/api/anthropic",
  fal: "/api/fal-ai",
  senuto: "/api/senuto",
  crawl4ai: "/crawl4ai",
  // Phase 3 C1 (2026-05-26): migracja na proxy. Basic auth wstrzykiwany przez
  // nginx z DATAFORSEO_BASIC_AUTH_{FIRMA|SPONSORAKI} per X-Workspace header.
  dataforseo: "/api/dataforseo",
};

// Node.js process.env access wrapped — `process` is undefined in browser
// (this constant ladowal sie w przegladarce -> ReferenceError -> React nie mount).
function nodeEnv(key: string): string | undefined {
  if (typeof process !== "undefined" && process.env) return process.env[key];
  return undefined;
}

const NODE_BASE: Record<ApiKey, string> = {
  openai: "https://api.openai.com",
  anthropic: "https://api.anthropic.com",
  fal: "https://fal.run",
  senuto: "https://api.senuto.com",
  // Internal Coolify network hostname (worker container -> crawl4ai container).
  // Override via process.env.CRAWL4AI_URL if needed.
  crawl4ai: nodeEnv("CRAWL4AI_URL") || "http://crawl4ai:11235",
  dataforseo: "https://api.dataforseo.com",
};

function nodeAuthHeaders(api: ApiKey): Record<string, string> {
  switch (api) {
    case "openai": {
      const key = process.env.OPENAI_API_KEY;
      return key ? { Authorization: `Bearer ${key}` } : {};
    }
    case "anthropic": {
      const key = process.env.ANTHROPIC_API_KEY;
      return key
        ? {
            "x-api-key": key,
            "anthropic-version": "2023-06-01",
          }
        : {};
    }
    case "fal": {
      const key = process.env.FAL_AI_API_KEY;
      return key ? { Authorization: `Key ${key}` } : {};
    }
    case "senuto": {
      const key = process.env.SENUTO_API_KEY;
      return key ? { Authorization: `Bearer ${key}` } : {};
    }
    case "dataforseo": {
      // Worker server-side: czyta DATAFORSEO_LOGIN/PASSWORD z process.env
      // (NIE VITE_ — to byly bundle keys, post-Phase3 wycofane).
      // Worker dostanie workspace przez job payload (Phase 3.5 backlog) —
      // wtedy moglby uzywac _FIRMA/_SPONSORAKI keys. Na razie legacy.
      const login = process.env.DATAFORSEO_LOGIN ?? "";
      const password = process.env.DATAFORSEO_PASSWORD ?? "";
      if (!login || !password) return {};
      const b64 = Buffer.from(`${login}:${password}`).toString("base64");
      return { Authorization: `Basic ${b64}` };
    }
    case "crawl4ai":
    default:
      return {};
  }
}

export function getApiBase(api: ApiKey): string {
  return isNodeRuntime() ? NODE_BASE[api] : BROWSER_BASE[api];
}

/**
 * Buduje headers dla danego API providera.
 *
 * - Node (worker): wstrzykuje Authorization z process.env (Phase 1 pattern)
 * - Browser: nginx wstrzykuje Authorization server-side. Frontend dodaje
 *   TYLKO X-Workspace (Phase 3 C2) zeby nginx map zmapowal na poprawny klucz
 *   per workspace (firma vs sideprojects).
 *
 * Crawl4AI: brak Authorization w obu trybach (Docker internal sidecar).
 */
export function getApiHeaders(api: ApiKey): Record<string, string> {
  if (isNodeRuntime()) {
    return nodeAuthHeaders(api);
  }
  // Browser path: nginx adds auth server-side. Tylko X-Workspace z localStorage.
  // Crawl4AI tez dostaje X-Workspace dla spojnosci (zignorowany przez sidecar).
  return { "X-Workspace": getActiveWorkspace() };
}

export function getApiTarget(api: ApiKey, path: string): ApiTarget {
  const base = getApiBase(api).replace(/\/$/, "");
  const p = path.startsWith("/") ? path : `/${path}`;
  return {
    url: `${base}${p}`,
    headers: getApiHeaders(api),
  };
}
